import { ChangeDetectionStrategy, Component, DestroyRef, effect, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router, RouterLink, RouterLinkActive, NavigationEnd } from '@angular/router';
import { filter } from 'rxjs';
import { IconComponent } from '../../shared/icon/icon.component';
import { SidebarService } from '../../services/sidebar.service';
import { RecommendationService } from '../../services/recommendation.service';
import { AuthService } from '../../services/auth.service';
import { ThemeService } from '../../services/theme.service';
import { NavItem } from '../../models/nav-item.model';

@Component({
  selector: 'app-sidebar',
  imports: [RouterLink, RouterLinkActive, IconComponent],
  templateUrl: './sidebar.component.html',
  styleUrl: './sidebar.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SidebarComponent {
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);
  public readonly sidebarService = inject(SidebarService);
  public readonly recommendationService = inject(RecommendationService);
  private readonly authService = inject(AuthService);
  public readonly themeService = inject(ThemeService);
  public collapsed = signal(false);
  public expandedItems = signal<Set<string>>(new Set());

  constructor() {
    effect(() => {
      document.documentElement.style.setProperty('--sidebar-width', this.collapsed() ? '80px' : '260px');
    });

    this.recommendationService.getRecommendations().pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe();

    this.router.events.pipe(
      filter(e => e instanceof NavigationEnd),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(() => {
      this.sidebarService.close();
    });
  }

  public readonly navItems: NavItem[] = [
    { id: 'dashboard', label: 'Dashboard', icon: 'home', route: '/dashboard' },
    { id: 'aanbevolen', label: 'Aanbevolen', icon: 'star', route: '/aanbevolen' },
    { id: 'favorieten', label: 'Favorieten', icon: 'heart', route: '/favorieten' },
    {
      id: 'bieden', label: 'Bieden', icon: 'gavel',
      children: [
        { id: 'bieden-niet-geboden', label: 'Niet geboden', route: '/bieden/niet-geboden' },
        { id: 'bieden-geboden', label: 'Geboden', route: '/bieden/geboden' },
      ]
    },
    {
      id: 'geboden', label: 'In behandeling', icon: 'clock',
      children: [
        { id: 'geboden-hoogste-bod', label: 'Hoogste bod', route: '/geboden/hoogste-bod' },
        { id: 'geboden-wel-geboden', label: 'Geboden', route: '/geboden/wel-geboden' },
        { id: 'geboden-niet-geboden', label: 'Niet geboden', route: '/geboden/niet-geboden' },
      ]
    },
    {
      id: 'mijn-voertuigen', label: 'Gekocht', icon: 'car',
      children: [
        { id: 'mv-te-verwachten', label: 'Te verwachten', route: '/mijn-voertuigen/te-verwachten' },
        { id: 'mv-afgeleverd', label: 'Afgeleverd', route: '/mijn-voertuigen/afgeleverd' },
      ]
    },
    { id: 'verkocht', label: 'Gemist', icon: 'package', route: '/verkocht' },
    { id: 'zoeken', label: 'Zoeken', icon: 'search', route: '/zoeken' },
    { id: 'statistieken', label: 'Statistieken', icon: 'chart', route: '/statistieken' },
  ];

  public toggleSidebar(): void {
    this.collapsed.update(v => !v);
  }

  public toggleExpand(item: NavItem): void {
    this.expandedItems.update(set => {
      if (set.has(item.id)) {
        return new Set();
      }
      return new Set([item.id]);
    });
  }

  public isExpanded(item: NavItem): boolean {
    if (this.expandedItems().size > 0) {
      return this.expandedItems().has(item.id);
    }
    return this.isParentActive(item);
  }

  public isParentActive(item: NavItem): boolean {
    if (!item.children) return false;
    const url = this.router.url.split('?')[0]; // Remove query parameters
    return item.children.some(child => url === child.route);
  }

  public logout(): void {
    this.authService.logout();
  }
}
